import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Typography, List } from "@mui/material";
import SSOApiService from "../../../services/SsoServices";
import { SUBSCRIBER_SUBSCRIPTIONS } from "../../../constants/api-constants";
import SubscriptionCard from "./SubscriptionCard";
import SubscriptionDetail from "./SubscriptionDetail";
import "./purchase.css";

const SELECTED_SUBSCRIPTION_KEY = "selected_subscription";

/*istanbul ignore next */
const SubscriptionList = ({ onViewingDetailChange }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState(() => {
    try {
      const saved = localStorage.getItem(SELECTED_SUBSCRIPTION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error("Failed to load selected subscription:", error);
      return null;
    }
  });
  const observerTarget = useRef(null);
  const pageNumberRef = useRef(1);
  const isLoadingRef = useRef(false);

  const loadSubscriptions = async (page, isInitial = false) => {
    if (isLoadingRef.current && !isInitial) return;

    try {
      isLoadingRef.current = true;

      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const payload = {
        pageSize: 10,
        pageNumber: page,
        ignorePaging: false,
      };

      const response = await SSOApiService.ssoPost(
        SUBSCRIBER_SUBSCRIPTIONS,
        payload,
      );
      if (response?.responseStatus?.isSuccess && response?.result?.records) {
        const newRecords = response.result.records;
        const totalRecs = response.result.totalRecords || 0;

        if (isInitial) {
          setSubscriptions(newRecords);
          setHasMore(newRecords.length < totalRecs);
        } else {
          setSubscriptions((prev) => {
            const updatedData = [...prev, ...newRecords];
            setHasMore(updatedData.length < totalRecs);
            return updatedData;
          });
        }
      } else {
        setError(
          response?.responseStatus?.description ||
            "Failed to load subscriptions",
        );
      }
    } catch (err) {
      console.error("Error loading subscriptions:", err);
      setError("Failed to load subscriptions. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  };

  useEffect(() => {
    pageNumberRef.current = 1;
    setSubscriptions([]);
    setHasMore(true);

    // Notify parent if we're starting with a selected subscription
    const savedSubscription = localStorage.getItem(SELECTED_SUBSCRIPTION_KEY);
    if (savedSubscription && onViewingDetailChange) {
      onViewingDetailChange(true);
    }

    loadSubscriptions(1, true);
  }, []);

  // Save selected subscription to localStorage whenever it changes
  useEffect(() => {
    try {
      if (selectedSubscription) {
        localStorage.setItem(
          SELECTED_SUBSCRIPTION_KEY,
          JSON.stringify(selectedSubscription),
        );
      } else {
        localStorage.removeItem(SELECTED_SUBSCRIPTION_KEY);
      }
    } catch (error) {
      console.error("Failed to save selected subscription:", error);
    }
  }, [selectedSubscription]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoadingRef.current &&
          !loading &&
          subscriptions.length > 0 &&
          !selectedSubscription
        ) {
          pageNumberRef.current += 1;
          loadSubscriptions(pageNumberRef.current);
        }
      },
      { threshold: 0.1, rootMargin: "100px" },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, subscriptions.length, selectedSubscription]);

  const handleSubscriptionClick = (subscription) => {
    setSelectedSubscription(subscription);
    // Scroll to top when opening subscription detail
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (onViewingDetailChange) {
      onViewingDetailChange(true);
    }
  };

  const handleBack = () => {
    setSelectedSubscription(null);
    localStorage.removeItem(SELECTED_SUBSCRIPTION_KEY);
    if (onViewingDetailChange) {
      onViewingDetailChange(false);
    }
  };

  const handleUpdate = () => {
    pageNumberRef.current = 1;
    setSubscriptions([]);
    setHasMore(true);
    loadSubscriptions(1, true);
  };

  if (loading) {
    return (
      <div
        className="sso-loader-wrapper"
        style={{ padding: "20px", color: "white" }}
      >
        <div className="sso-loader" />
        Loading Subscriptions...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "white" }}>
        <Typography variant="body1">
          Unable to load subscriptions. Please try again later.
        </Typography>
      </div>
    );
  }

  if (selectedSubscription) {
    return (
      <SubscriptionDetail
        subscription={selectedSubscription}
        onBack={handleBack}
        onUpdate={handleUpdate}
      />
    );
  }

  if (!subscriptions || subscriptions.length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "white" }}>
        <Typography variant="h6">No active subscriptions available</Typography>
      </div>
    );
  }

  return (
    <div className="subscription-list">
      <List className="subscription-list-container">
        {subscriptions.map((subscription) => (
          <SubscriptionCard
            key={subscription.userSubscriptionId || subscription.requestId}
            subscription={subscription}
            onClick={handleSubscriptionClick}
          />
        ))}
      </List>
      {hasMore && <div ref={observerTarget} style={{ height: "20px" }} />}
      {loadingMore && (
        <div
          className="sso-loader-wrapper"
          style={{ padding: "20px", color: "white" }}
        >
          <div className="sso-loader" />
          Loading more subscriptions...
        </div>
      )}
    </div>
  );
};

SubscriptionList.propTypes = {
  onViewingDetailChange: PropTypes.func,
};

export default SubscriptionList;
